import { type ReactNode } from "react";
import { cn } from "@/lib/cn";

type Tone = "default" | "primary" | "secondary" | "inferred" | "ok" | "warn" | "danger" | "info" | "amber" | "purple" | "muted";

const toneClasses: Record<Tone, string> = {
  default: "bg-[var(--color-surface-2)] text-[var(--color-ink-muted)] border-[var(--color-border)]",
  primary: "bg-[rgba(94,234,212,0.12)] text-[var(--color-primary)] border-[rgba(94,234,212,0.3)]",
  secondary: "bg-[rgba(251,191,36,0.12)] text-[var(--color-secondary)] border-[rgba(251,191,36,0.3)]",
  inferred: "bg-[rgba(167,139,250,0.12)] text-[var(--color-inferred)] border-[rgba(167,139,250,0.32)]",
  ok: "bg-[rgba(52,211,153,0.12)] text-[var(--color-support)] border-[rgba(52,211,153,0.3)]",
  warn: "bg-[rgba(251,191,36,0.12)] text-[var(--color-secondary)] border-[rgba(251,191,36,0.3)]",
  danger: "bg-[rgba(244,63,94,0.12)] text-[var(--color-danger)] border-[rgba(244,63,94,0.3)]",
  info: "bg-[rgba(96,165,250,0.12)] text-[var(--color-define)] border-[rgba(96,165,250,0.3)]",
  amber: "bg-[rgba(251,191,36,0.12)] text-[var(--color-undercut)] border-[rgba(251,191,36,0.3)]",
  purple: "bg-[rgba(167,139,250,0.12)] text-[var(--color-reframe)] border-[rgba(167,139,250,0.3)]",
  muted: "bg-[var(--color-surface-2)] text-[var(--color-ink-faint)] border-[var(--color-border)]",
};

export function Badge({
  children, tone = "default", className, icon, mono = true, style, ...rest
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
  icon?: ReactNode;
  mono?: boolean;
  style?: React.CSSProperties;
  [key: string]: unknown;
}) {
  return (
    <span
      style={style}
      className={cn(
        "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] uppercase tracking-wider leading-none",
        mono && "font-mono",
        toneClasses[tone],
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </span>
  );
}

export function TagChip({ children, onClick, active }: { children: ReactNode; onClick?: () => void; active?: boolean }) {
  const Comp = onClick ? "button" : "span";
  return (
    <Comp
      onClick={onClick}
      className={cn(
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs border transition-colors",
        onClick && "cursor-pointer hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]",
        active
          ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-[rgba(94,234,212,0.1)]"
          : "border-[var(--color-border)] text-[var(--color-ink-muted)] bg-[var(--color-surface-2)]",
      )}
    >
      #{children}
    </Comp>
  );
}
